/* eslint-disable @typescript-eslint/ban-ts-comment */
import { gql } from '@apollo/client';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { AuthEventTypes, WindowMessageData } from '@app/types';

import { Platform } from '@mfe/shared/schema-types';

import { AuthUserState, selectUser, setUser } from '../auth';
import { selectConfig } from '../config';
import { sendMessageToMobileApp } from '../crossPlatformCommunication';
import { selectEvent } from '../events';
import { graphqlQueryWithErrors } from '../graphql';
import { selectUserInfo, setUserInfo } from '../userInfo';
import { isPWAMyViasat } from '../../utils';
import {
  goToUrl,
  openInNewTab as openInNewTabAction,
  postMessage as postMessageAction,
  refreshContext as refreshContextAction,
  scrollToTop,
} from './utilsSlice';
import { SagaInput } from './types';

enum AllowedEvents {
  'init',
  'loaded',
  'resize',
  'GetToken',
  'scroll-top',
  'token-valid',
  'invalid-or-expired-token',
}

const validateAuthEvents = (eventType: AuthEventTypes | AllowedEvents): boolean => {
  return eventType in AuthEventTypes || eventType in AllowedEvents;
};

const postMessage = (data: WindowMessageData, eventOrigin: string | undefined) => {
  const message = { ...data };
  if (!eventOrigin && !validateAuthEvents(message.eventType as AuthEventTypes)) {
    console.error('Invalid origin or invalid event', eventOrigin, message.eventType);
    return;
  }
  const origin = eventOrigin ?? '*';

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window.ReactNativeWebView && !isPWAMyViasat()) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  } else {
    window.parent.postMessage(message, origin);
  }
};

function* postMessageSaga({ payload }: { type: string; payload: WindowMessageData }) {
  const {
    user: {
      auth: { subject },
    },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  // NOTE: Do not remove or replace this line, if you have to do so please coordinate with a GAC personel to understand the implications,
  // and let them know of the changes. The origin here is being used as a middleware to handle the origin of the parent window.
  const { origin }: ReturnType<typeof selectEvent> = yield select(selectEvent);

  yield call(
    postMessage,
    {
      ...payload,
      subject,
    } as WindowMessageData,
    origin
  );
}

export function* waitForToken() {
  const user: AuthUserState = yield select(selectUser);
  if (user.loading) {
    yield take(setUser.type);
  }
}

export function* waitForUserInfo() {
  const { loading } = yield select(selectUserInfo);

  if (loading) {
    yield take(setUserInfo.type);
  }
}

function* openInNewTab({ payload }: { type: string; payload: string }) {
  const { platform } = yield select(selectConfig);
  if (!payload) return;

  if (platform === Platform.Web) {
    window.open(payload, '_blank');
  } else {
    yield put(
      sendMessageToMobileApp({
        type: 'open-in-new-tab',
        data: { url: payload },
      })
    );
  }
}

const REFRESH_CONTEXT = gql`
  query getRefreshContext {
    getRefreshContext
  }
`;

function* refreshContext() {
  yield call(graphqlQueryWithErrors, {
    query: REFRESH_CONTEXT,
    fetchPolicy: 'no-cache',
  });
}

function handleLowerEnvLogging(action: Action | PayloadAction<any>) {
  if (process.env['NODE_ENV'] !== 'production') {
    console.log(action);
  }
}

function handleGoToUrl({ payload }: SagaInput<typeof goToUrl>) {
  window.location.assign(payload);
}

function handleScrollToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
}

/**
 * Recursively removes the `__typename` field from an object or array of objects.
 * This is useful when working with GraphQL responses where `__typename` is included
 * but not needed for further processing or mutation inputs.
 */

export function omitTypenameDeep<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(omitTypenameDeep) as T;
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => key !== '__typename')
        .map(([key, value]) => [key, omitTypenameDeep(value)])
    ) as T;
  } else {
    return obj;
  }
}

export function* watchUtils() {
  yield spawn(function* () {
    yield all([
      takeLatest(postMessageAction.type, postMessageSaga),
      takeLatest(refreshContextAction.type, refreshContext),
      takeLatest(openInNewTabAction.type, openInNewTab),
      takeEvery('*', handleLowerEnvLogging),
      takeLatest(goToUrl.type, handleGoToUrl),
      takeLatest(scrollToTop.type, handleScrollToTop),
    ]);
  });
}
